import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Users, Star, TrendingUp, Edit, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCustomers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    isVIP: false,
    vipDiscount: 0,
    notes: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/api/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setEditForm({
      isVIP: customer.isVIP || false,
      vipDiscount: customer.vipDiscount || 0,
      notes: customer.notes || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveCustomer = async () => {
    try {
      await axios.put(`${API}/api/customers/${selectedCustomer.id}`, editForm);
      toast.success('Client mis à jour avec succès');
      setShowEditDialog(false);
      fetchCustomers();
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleViewDetails = async (customer) => {
    try {
      const response = await axios.get(`${API}/api/customers/${customer.id}`);
      setSelectedCustomer(response.data);
      setShowEditDialog(true);
      setEditForm({
        isVIP: response.data.isVIP || false,
        vipDiscount: response.data.vipDiscount || 0,
        notes: response.data.notes || ''
      });
    } catch (error) {
      console.error('Error fetching customer details:', error);
      toast.error('Erreur lors du chargement des détails');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 flex items-center justify-center">
        <p className="text-white text-xl">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              className="border-gold/30 text-gold hover:bg-gold/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="text-4xl font-bold text-white flex items-center gap-3">
              <Users className="w-10 h-10 text-gold" />
              Gestion des Clients
            </h1>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-gray-900 to-black border-gold/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Clients</p>
                  <p className="text-3xl font-bold text-white">{customers.length}</p>
                </div>
                <Users className="w-12 h-12 text-gold/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-900 to-black border-gold/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Clients VIP</p>
                  <p className="text-3xl font-bold text-gold">
                    {customers.filter(c => c.isVIP).length}
                  </p>
                </div>
                <Star className="w-12 h-12 text-gold/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-900 to-black border-gold/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">CA Total</p>
                  <p className="text-3xl font-bold text-white">
                    {customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0).toFixed(0)}€
                  </p>
                </div>
                <TrendingUp className="w-12 h-12 text-gold/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customers Table */}
        <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-gold/30">
          <CardHeader>
            <CardTitle className="text-2xl text-white">Liste des Clients</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-gold/20 hover:bg-transparent">
                  <TableHead className="text-gold">Nom</TableHead>
                  <TableHead className="text-gold">Email</TableHead>
                  <TableHead className="text-gold">Téléphone</TableHead>
                  <TableHead className="text-gold">Statut</TableHead>
                  <TableHead className="text-gold">Courses</TableHead>
                  <TableHead className="text-gold">Total Dépensé</TableHead>
                  <TableHead className="text-gold">Points Fidélité</TableHead>
                  <TableHead className="text-gold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow key={customer.id} className="border-gold/10 hover:bg-gold/5">
                    <TableCell className="text-white font-medium">{customer.name}</TableCell>
                    <TableCell className="text-gray-400">{customer.email}</TableCell>
                    <TableCell className="text-gray-400">{customer.phone}</TableCell>
                    <TableCell>
                      {customer.isVIP ? (
                        <Badge className="bg-gold text-black">
                          <Star className="w-3 h-3 mr-1" />
                          VIP ({customer.vipDiscount}%)
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-gray-600 text-gray-400">
                          Standard
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-white">{customer.totalBookings || 0}</TableCell>
                    <TableCell className="text-white">{(customer.totalSpent || 0).toFixed(2)}€</TableCell>
                    <TableCell className="text-white">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-gold" />
                        {customer.loyaltyPoints || 0}
                        {customer.loyaltyPoints >= 5 && (
                          <span className="text-xs text-green-400">(Récompense !)</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          onClick={() => handleViewDetails(customer)}
                          size="sm"
                          variant="outline"
                          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleEditCustomer(customer)}
                          size="sm"
                          variant="outline"
                          className="border-gold/30 text-gold hover:bg-gold/10"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {customers.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">Aucun client enregistré</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Customer Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-gray-900 border-gold/30 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl text-gold">
                Gérer Client: {selectedCustomer?.name}
              </DialogTitle>
            </DialogHeader>

            {selectedCustomer && (
              <div className="space-y-6">
                {/* Customer Info */}
                <div className="bg-gray-800/50 p-4 rounded-lg">
                  <p className="text-gray-400"><strong>Email:</strong> {selectedCustomer.email}</p>
                  <p className="text-gray-400"><strong>Téléphone:</strong> {selectedCustomer.phone}</p>
                  <p className="text-gray-400"><strong>Courses:</strong> {selectedCustomer.totalBookings || 0}</p>
                  <p className="text-gray-400"><strong>Total dépensé:</strong> {(selectedCustomer.totalSpent || 0).toFixed(2)}€</p>
                  <p className="text-gray-400"><strong>Points fidélité:</strong> {selectedCustomer.loyaltyPoints || 0}</p>
                </div>

                {/* VIP Status */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="isVIP"
                      checked={editForm.isVIP}
                      onChange={(e) => setEditForm({ ...editForm, isVIP: e.target.checked })}
                      className="w-5 h-5 rounded border-gold text-gold focus:ring-gold"
                    />
                    <label htmlFor="isVIP" className="text-lg font-semibold cursor-pointer flex items-center gap-2">
                      <Star className="w-5 h-5 text-gold" />
                      Statut VIP
                    </label>
                  </div>

                  {editForm.isVIP && (
                    <div className="space-y-2 pl-8">
                      <Label htmlFor="vipDiscount" className="text-white">
                        Réduction VIP (%)
                      </Label>
                      <Input
                        id="vipDiscount"
                        type="number"
                        min="0"
                        max="100"
                        value={editForm.vipDiscount}
                        onChange={(e) => setEditForm({ ...editForm, vipDiscount: parseFloat(e.target.value) })}
                        className="bg-gray-800 border-gold/30 text-white"
                      />
                      <p className="text-xs text-gray-400">
                        Ce client bénéficiera de {editForm.vipDiscount}% de réduction sur toutes ses courses
                      </p>
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-white">Notes Admin</Label>
                  <textarea
                    id="notes"
                    rows={3}
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    className="w-full bg-gray-800 border border-gold/30 text-white rounded-md p-3"
                    placeholder="Notes internes sur ce client..."
                  />
                </div>

                {/* Booking History */}
                {selectedCustomer.bookings && selectedCustomer.bookings.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold text-gold">Historique des Réservations</h3>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {selectedCustomer.bookings.map((booking) => (
                        <div key={booking.id} className="bg-gray-800/50 p-3 rounded-lg">
                          <div className="flex justify-between">
                            <span className="text-white">{booking.id}</span>
                            <Badge className={
                              booking.status === 'completed' ? 'bg-green-500' :
                              booking.status === 'confirmed' ? 'bg-blue-500' :
                              booking.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
                            }>
                              {booking.status}
                            </Badge>
                          </div>
                          <p className="text-gray-400 text-sm">{booking.date} - {booking.pickup}</p>
                          <p className="text-white font-semibold">{booking.estimatedPrice}€</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 justify-end">
                  <Button
                    onClick={() => setShowEditDialog(false)}
                    variant="outline"
                    className="border-gray-600 text-gray-400"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handleSaveCustomer}
                    className="bg-gradient-to-r from-gold to-amber-600 text-black hover:from-amber-600 hover:to-gold"
                  >
                    Enregistrer
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminCustomers;
